import { UserClientService } from "../../../services/authentication/user-client.service";
import { UserModel, PersistUserRequest, UserPhoneNumberModel } from "../../../models/manage/user.models";
import { autoinject, BindingEngine, computedFrom, useView } from "aurelia-framework";
import { UnitOfWorkObserver } from "../../../utilities/unit-of-work-observer";
import { ManageRightShipUsersClientService } from "../../../services/modules/righship-qi-manage-users.service";
import { NotificationClientService, NotificationType } from "../../../services/basics/notification-client.service";
import { I18N } from "aurelia-i18n";
import { ValidationsClientService } from "../../../services/validations/validations-client.service";
import { UserClaimsService } from "../../../services/authentication/user-claims.service";
import { Claims } from "../../../constants/authentication/claims";
import { CoachmarkClientService } from "../../../services/coachmark/coachmark-client.service";
import { CultureInfoService } from "../../../services/internationalization/culture-info.service";
import { CultureInfo } from "../../../models/internationalization/culture-info.models";
import * as momentTZ from "moment-timezone";
 import * as IntlTelInput from "intl-tel-input";
import { LookupValue, LookupValueRequest } from "../../../models/lookups/lookup.models";
import { LookupClientService } from "../../../services/lookups/lookup-client.service";
import { Reflection } from "../../../utilities/reflection";

@autoinject
@useView('views/pages/settings/profile.html')
export class Profile {

    public changingPassword: boolean = false;
    public userInEdit: UserModel = null;
    public isAdUser: boolean = false;
    public dummyPassword = "00000000";
    public _userInEditUow: UnitOfWorkObserver;
    public newPassword: string;
    public confirmPassword: string;
    public phoneNumberTypeId: number;
    public businessUnitName: string;
    public hasMfa: boolean = false;
    isRightshipUser: boolean;

    public cultures: CultureInfo[] = [];
    public timeZones: string[] = [];

    telInput: IntlTelInput.Plugin = null;
    public phoneCodes: LookupValue[];
    public roleName: string;

    constructor(private userClientService: UserClientService, private bindingEngine: BindingEngine,
        private manageUsersClientService: ManageRightShipUsersClientService, private notificationService: NotificationClientService,
        private i18N: I18N, private validationsClientService: ValidationsClientService, userCliamsService: UserClaimsService,
        private coachmarkService: CoachmarkClientService, private cultureInfoService: CultureInfoService,
        private lookupsClientService: LookupClientService
        ) {
        this.isRightshipUser = userCliamsService.hasPermission(Claims.RightShipUser);
        this.cultures = this.cultureInfoService.cultures;
        this.timeZones = momentTZ.tz.names();

        this.roleName = this.userClientService.currentUser.roleName;
    }

    activate() {
        const userPromise = this.loadUser();
        const phoneCodePromise = this.loadPhoneCodes();

        Promise.all([userPromise, phoneCodePromise]).then(() =>{

            if(this.phoneNumberCountryCode && this.phoneNumberCountryCode > 0)
            {
                const currentCode = this.phoneCodes.filter(x => x.id == this.phoneNumberCountryCode);
                if(currentCode && currentCode.length > 0)
                    this.telInput.setCountry(currentCode[0].additionalData.country);
            }
        });
    }

    attached()
    {
        this.initilizeIntlTelInput();
    }

    detached() {
        if (this._userInEditUow) {
            this._userInEditUow.dispose();
        }
        this._userInEditUow = null;
        this.userInEdit = null;
    }

    async loadUser() {
        
        const userResponse = await this.manageUsersClientService.getMe();

        this.hasMfa = userResponse.isUserMfaEnabled;

        this.isAdUser = userResponse.isUserAzureAD;

        this.userInEdit = userResponse.user;

        this.userInEdit.officeLocationId = await this.manageUsersClientService.getOfficeLocationOfUser(userResponse.user.userId);
        
        if (userResponse.businessUnit)
            this.businessUnitName = userResponse.businessUnit.businessUnitName;

        if (this._userInEditUow) {
            this._userInEditUow.dispose();
        }
        this._userInEditUow = UnitOfWorkObserver.getTrackingInstance(this.bindingEngine, this.userInEdit);


        //set defualt langauge
        if (!this.userInEdit.cultureCode) {
            let defaultCulture = this.cultureInfoService.findByCode(window.navigator['userLanguage'] || window.navigator.language);
            defaultCulture = defaultCulture != null ? defaultCulture : this.cultureInfoService.findByCode("en-GB");
            this.userInEdit.cultureCode = defaultCulture.code;
        }
    }

    @computedFrom("userInEdit", "userInEdit.userPhoneNumbers.length")
    get userPhoneNumber() {
        if (this.userInEdit && this.userInEdit.userPhoneNumbers && this.userInEdit.userPhoneNumbers.length > 0) {
            return this.userInEdit.userPhoneNumbers[0];
        }
    }

    @computedFrom("userPhoneNumber", "userPhoneNumber.phoneNumber")
    get phoneNumber(): string {
        if (this.userPhoneNumber) {
            return this.userPhoneNumber.phoneNumber;
        }
        return null;
    }

    @computedFrom("userPhoneNumber", "userPhoneNumber.phoneNumberCodeId")
    get phoneNumberCountryCode(): number {
        if (this.userPhoneNumber) {
            return this.userPhoneNumber.phoneNumberCodeId;
        }
        return null;
    }

    set phoneNumberCountryCode(value: number) {

        if (!this.userInEdit.userPhoneNumbers) this.userInEdit.userPhoneNumbers = [];

        if (!value && !this.phoneNumber) {
            //if both phone number and phone country code have been removed, the phone number needs to be removed from list
            //otherwise it tries to save with empty phone number code
            this.userInEdit.userPhoneNumbers = this.userInEdit.userPhoneNumbers.splice(0, 1);
        }
        else if (this.userInEdit.userPhoneNumbers.length == 0) {
            this.userInEdit.userPhoneNumbers.push(<UserPhoneNumberModel>{ phoneNumber: null, phoneNumberCodeId: value, phoneNumberTypeId: this.phoneNumberTypeId, phoneSequence: 0 });
        }
        else {
            this.userPhoneNumber.phoneNumberCodeId = value;
        }

        if(this.userPhoneNumber && !this.userPhoneNumber.phoneNumber && !this.userPhoneNumber.phoneNumberCodeId)
        {
            this.userInEdit.userPhoneNumbers.splice(0, 1);
        }
    }

    set phoneNumber(value: string) {
        if (!this.userInEdit.userPhoneNumbers) this.userInEdit.userPhoneNumbers = [];

        if (this.userInEdit.userPhoneNumbers.length == 0) {
            this.userInEdit.userPhoneNumbers.push(<UserPhoneNumberModel>{ phoneNumber: value, phoneNumberCodeId: null, phoneNumberTypeId: this.phoneNumberTypeId, phoneSequence: 0 });
        }
        else {
            this.userPhoneNumber.phoneNumber = value;
        }

        if(this.userPhoneNumber && !this.userPhoneNumber.phoneNumber && !this.userPhoneNumber.phoneNumberCodeId)
        {
            this.userInEdit.userPhoneNumbers.splice(0, 1);
        }
    }

    save() {

        return new Promise<void>((resolve, reject) => {
            this.userInEdit.fullName = this.userInEdit.firstName + ' ' + this.userInEdit.lastName;

            setTimeout(() => {
                const changes = this._userInEditUow.getChanges();
                this.manageUsersClientService.save(<PersistUserRequest>{
                    changes: changes,
                    id: this.userInEdit.userId,
                    newPassword: this.changingPassword ? this.newPassword : null,
                    passwordConfirmation: this.changingPassword ? this.confirmPassword : null
                }).then(x => {
                    if (x.validationResult && x.validationResult.hasError) {
                        this.validationsClientService.showValidationErrors(x.validationResult);
                        reject();
                    }
                    else {
                        if (changes["timeZone"] || changes["cultureCode"]) {
                            this.refreshRegionCache();
                        }
                        this._userInEditUow.clearChanges();
                        this.loadUser();
                        this.notificationService.show(this.i18N.tr('user-successfully-saved-text'), "", NotificationType.Success);
                        this.changingPassword = false;
                        resolve();
                    }
                }).catch(() => {
                    reject();
                });
            });
        });
    }

    @computedFrom(Reflection.getPropertyPath<Profile>(z => z._userInEditUow.changes.unitOfWorks),
        Reflection.getPropertyPath<Profile>(z => z._userInEditUow),
        Reflection.getPropertyPath<Profile>(z => z._userInEditUow.unitOfWorks))
    get hasChanges(): boolean { 
        if (!this._userInEditUow) return false; 
        return this._userInEditUow.hasChanges(true);
    }

    changePassword() {
        this.changingPassword = !this.changingPassword;
    }

    resetMyCoachmarks() {
        return new Promise<void>((resolve, reject) => {
            this.coachmarkService.resetMyCoachmarks().then(() => {
                resolve();
                this.notificationService.show("Reset Coachmark Successfully", '', NotificationType.Success);
            }).catch(e => {
                reject(e);
            });
        });
    }

    resetMfaEnrollment() {
        return new Promise<void>((resolve, reject) => {
            this.manageUsersClientService.resetUserMfaEnrollment().then(() => {
                resolve();
                this.notificationService.show("Reset MFA Enrollment Successfully", '', NotificationType.Success);
            }).catch(e => {
                reject(e);
            });
        });
    }

    refreshRegionCache() {
        const user = this.userClientService.currentUser;
        user.cultureCode = this.userInEdit.cultureCode;
        user.timezone = this.userInEdit.timeZone;
        this.userClientService.refreshUserRegionSettings();
    }

    async loadPhoneCodes() {
        const request = new LookupValueRequest();
        request.lookupValueQuery = "PhoneCodes";        

        const countriesData = window["intlTelInputGlobals"].getCountryData().sort((a,b) => {
            if(a["priority"] > b["priority"]) return 1;
            if(a["priority"] < b["priority"]) return -1;
            return 0;
        });

        //add empty countycode to be able to deselect code
        if(countriesData.filter(x => x.iso2 == "emtpy" && x.name == "").length == 0)
        {
            const empty = {
                name: "",
                iso2: "emtpy",
                dialCode: ""
            }
            countriesData.unshift(empty);
        }

        const lookupResponse = await this.lookupsClientService.getLookup(request, 'default')
        this.phoneCodes = lookupResponse.activeValues.map(v => {
            const country = countriesData.filter(c => ('+' + c.dialCode.trim()) == v.name.trim());
            if(country && country.length > 0)
            {
                v.additionalData.country = country[0].iso2;
            }
            return v;
        });
    }

    initilizeIntlTelInput()
    {
        const input = document.querySelector("#phone-number");
        this.telInput = IntlTelInput(input, {
            hiddenInput: "full",
            separateDialCode: true,
            preferredCountries: ["gb","us","sg","au"],
            utilsScript: "https://cdn.jsdelivr.net/npm/intl-tel-input@16.0.3/build/js/utils.js"
        });

        input.removeEventListener("countrychange", this.countryChanged.bind(this));
        input.addEventListener("countrychange", this.countryChanged.bind(this));
        this.telInput.setCountry('');

        if(this.phoneCodes && this.phoneNumberCountryCode && this.phoneNumberCountryCode > 0)
        {
            const currentCode = this.phoneCodes.filter(x => x.id == this.phoneNumberCountryCode);
            if(currentCode && currentCode.length > 0)
                this.telInput.setCountry(currentCode[0].additionalData.country);
        }
    }

    countryChanged()
    {
        const countryData = this.telInput.getSelectedCountryData();
        if(countryData && countryData.iso2 == "emtpy" && countryData.name == "")
        {
            this.phoneNumberCountryCode = null;
        }

        if(countryData && countryData.dialCode)
        {
            const codes = this.phoneCodes.filter(x => x.name.trim() == ('+' + countryData.dialCode.trim()));

            if(codes.length > 0 && codes[0] && codes[0].id > 0)
            {
                if(codes[0].id != this.phoneNumberCountryCode)
                {
                    this.phoneNumberCountryCode = codes[0].id;
                }
            }
            else console.error("Phone code not found!");
        }
    }
}